<template>
  <div class="ncrm-board__modules">
    <button
      v-for="mod in items"
      :key="mod.route"
      class="ncrm-module-tile"
      :style="{ '--tile-color': mod.color, '--tile-alert-color': mod.alertColor }"
      @click="$emit('navigate', mod.route)"
    >
      <div class="ncrm-module-tile__top">
        <div class="ncrm-module-tile__icon-wrap">
          <v-icon size="18" class="ncrm-module-tile__icon">{{ mod.icon }}</v-icon>
        </div>
        <span
          v-if="mod.stats[1].value != null && mod.stats[1].value > 0"
          class="ncrm-module-tile__badge"
        >
          <v-icon size="10" class="ncrm-module-tile__badge-icon">mdi-alert-circle-outline</v-icon>{{ mod.stats[1].value }}
        </span>
        <v-skeleton-loader
          v-else-if="loading && mod.stats[1].value == null"
          type="text"
          width="28"
          height="16"
          class="d-inline-block"
        />
      </div>

      <div class="ncrm-module-tile__main">
        <div class="ncrm-module-tile__total">
          <template v-if="mod.stats[0].value != null">{{ formatTotal(mod.stats[0].value) }}</template>
          <v-skeleton-loader v-else-if="loading" type="text" width="40" height="22" class="d-inline-block" />
          <template v-else>-</template>
        </div>
        <div class="ncrm-module-tile__name">{{ mod.label }}</div>
      </div>

      <div class="ncrm-module-tile__bar-track">
        <div
          class="ncrm-module-tile__bar-fill"
          :style="{ width: computeModuleHealth(mod) + '%' }"
        />
      </div>
    </button>
  </div>
</template>

<script>
import { formatDisplayNumber } from "@/Utile/displayFormat.mjs";
const parseNumeric = (value, fallback = 0) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

export default {
  name: "NcrmBoardModuleTiles",
  props: {
    items: {
      type: Array,
      default: () => [],
    },
    loading: {
      type: Boolean,
      default: false,
    },
  },
  methods: {
    formatTotal(value) {
      return formatDisplayNumber(Number(value));
    },
    computeModuleHealth(mod) {
      const total = mod.stats?.[0]?.value;
      const alert = mod.stats?.[1]?.value;
      if (total == null) return 0;
      if (total === 0) return 100;
      const alertNum = parseNumeric(alert, 0);
      return Math.max(0, Math.min(100, (1 - alertNum / total) * 100));
    },
  },
};
</script>

<style scoped>
.ncrm-board__modules {
  display: grid;
  grid-template-columns: repeat(6, minmax(0, 1fr));
  gap: 10px;
}

.ncrm-module-tile {
  --tile-color: rgb(var(--v-theme-tertiary));
  --tile-alert-color: rgb(var(--v-theme-error));
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 14px 16px;
  border: 1px solid rgba(var(--v-theme-on-surface), 0.09);
  border-radius: var(--app-radius-base);
  background: var(--app-content-background);
  cursor: pointer;
  text-align: left;
  transition: border-color 0.15s ease, box-shadow 0.15s ease, transform 0.15s ease;
}

.ncrm-module-tile:hover {
  transform: translateY(-1px);
  border-color: color-mix(in srgb, var(--tile-color, rgb(var(--v-theme-tertiary))) 40%, transparent);
  box-shadow: 0 4px 16px color-mix(in srgb, var(--tile-color, rgb(var(--v-theme-tertiary))) 12%, transparent);
}

@supports not (color: color-mix(in srgb, red 50%, transparent)) {
  .ncrm-module-tile:hover {
    border-color: rgba(var(--v-theme-tertiary), 0.35);
    box-shadow: var(--app-shadow-md);
  }
}

.ncrm-module-tile__top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 6px;
}

.ncrm-module-tile__icon-wrap {
  width: 32px;
  height: 32px;
  border-radius: calc(var(--app-radius-base) - 2px);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  background: color-mix(in srgb, var(--tile-color) 10%, transparent);
}

.ncrm-module-tile__icon {
  color: var(--tile-color);
}

.ncrm-module-tile__badge {
  font-size: 0.68rem;
  font-weight: 700;
  padding: 2px 7px;
  border-radius: var(--app-radius-pill);
  display: inline-flex;
  align-items: center;
  white-space: nowrap;
  background: color-mix(in srgb, var(--tile-alert-color) 10%, transparent);
  color: var(--tile-alert-color);
}

.ncrm-module-tile__badge-icon {
  margin-right: 2px;
}

.ncrm-module-tile__main {
  display: flex;
  flex-direction: column;
  gap: 3px;
}

.ncrm-module-tile__total {
  font-size: 1.4rem;
  font-weight: 800;
  color: var(--app-text-strong);
  line-height: 1;
  font-variant-numeric: tabular-nums;
}

.ncrm-module-tile__name {
  font-size: 0.75rem;
  color: var(--app-text-default);
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.ncrm-module-tile__bar-track {
  height: 3px;
  border-radius: var(--app-radius-pill);
  background: rgba(var(--v-theme-on-surface), 0.07);
  overflow: hidden;
}

.ncrm-module-tile__bar-fill {
  height: 100%;
  border-radius: var(--app-radius-pill);
  background: var(--tile-color);
  transition: width 0.6s ease;
}

@supports not (color: color-mix(in srgb, red 50%, transparent)) {
  .ncrm-module-tile__icon-wrap {
    background: rgba(var(--v-theme-tertiary), 0.1);
  }

  .ncrm-module-tile__badge {
    background: rgba(var(--v-theme-error), 0.1);
  }
}

@media (max-width: 1100px) {
  .ncrm-board__modules {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
}
</style>
